import { useMemo } from 'react';
import {
  categorizeTokens,
  isLongFormToken,
  tokensStillMissing,
  type CampaignBindings,
  type CategorizedGroup,
} from './tokens';

/* TemplateInputsForm — Sprint 16b.

   Manifest-driven form on the campaign Settings step. The campaign's chosen
   templates declare what tokens they need (`merge_fields`); this form asks
   for any token not auto-resolved (recipient/recruiter/company/calendar).

   The form is fully controlled — the parent owns the `value` dict
   (campaign.merge_context) and the form calls `onChange` with the next
   full dict on every keystroke. Parent debounces persistence.

   Pure component: no hooks beyond `useMemo`. Takes the token list pre-
   computed by the parent (which does the templates fetch + collection)
   so testing doesn't need a QueryClientProvider. */

export interface TemplateInputsFormProps {
  /** Full list of unresolved tokens — produced by collectTemplateTokens
   *  upstream. The form does its own categorization + filtering. */
  tokens: string[];
  /** Current per-campaign merge_context dict (or null). */
  value: Record<string, string> | null;
  onChange: (next: Record<string, string>) => void;
  /** Optional helper text shown above the form. */
  intro?: string;
  /** Active campaign bindings that auto-resolve token families (Sprint 16c
   *  starts with `bound_job_id`). Tokens covered by an active binding
   *  are filtered out of the form so the recruiter doesn't fill them
   *  twice. */
  bindings?: CampaignBindings;
}

export function TemplateInputsForm({ tokens, value, onChange, intro, bindings }: TemplateInputsFormProps) {
  const groups: CategorizedGroup[] = useMemo(() => categorizeTokens(tokens, bindings), [tokens, bindings]);
  const safeValue = value ?? {};
  const missing = useMemo(
    () => tokensStillMissing(groups.flatMap((g) => g.tokens), safeValue),
    [groups, safeValue],
  );

  const setToken = (token: string, next: string) => {
    onChange({ ...safeValue, [token]: next });
  };

  if (groups.length === 0) {
    return (
      <section data-testid="template-inputs-empty" style={shell()}>
        <h2 style={heading()}>Template inputs</h2>
        <p style={muted()}>
          No template inputs needed: every token the chosen templates use is automatically resolved
          from the recipient, your recruiter profile, the brand kit, or your default booking link.
        </p>
      </section>
    );
  }

  return (
    <section data-testid="template-inputs-form" style={shell()}>
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <h2 style={heading()}>Template inputs</h2>
        <span data-testid="template-inputs-missing-count" style={badge(missing.length === 0)}>
          {missing.length === 0 ? 'All filled' : `${missing.length} to fill`}
        </span>
      </header>
      {intro && <p style={{ ...muted(), marginBottom: 16 }}>{intro}</p>}
      {groups.map((group) => (
        <fieldset
          key={group.category}
          data-testid={`group-${group.category}`}
          style={{
            border: 'none',
            padding: 0,
            margin: '0 0 20px',
          }}
        >
          <legend style={legend()}>{group.label}</legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {group.tokens.map((token) => (
              <TokenField
                key={token}
                token={token}
                value={safeValue[token] ?? ''}
                onChange={(v) => setToken(token, v)}
              />
            ))}
          </div>
        </fieldset>
      ))}
    </section>
  );
}

function TokenField({ token, value, onChange }: { token: string; value: string; onChange: (v: string) => void }) {
  const longForm = isLongFormToken(token);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 11, color: 'var(--color-text-muted, #6b6b7d)' }}>
        {`{{${token}}}`}
      </span>
      {longForm ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          data-testid={`tif-${token}`}
          style={{ ...input(), fontFamily: 'inherit', resize: 'vertical', minHeight: 80 }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`tif-${token}`}
          style={input()}
        />
      )}
    </label>
  );
}

function shell(): React.CSSProperties {
  return {
    background: 'var(--card-bg, #fff)',
    border: '1px solid var(--card-border, #ececf2)',
    borderRadius: 'var(--card-radius, 8px)',
    padding: 20,
  };
}

function heading(): React.CSSProperties {
  return {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--color-text-brand, #1e1b4b)',
  };
}

function muted(): React.CSSProperties {
  return {
    margin: 0,
    fontSize: 13,
    color: 'var(--color-text-muted, #6b6b7d)',
    lineHeight: 1.5,
  };
}

function legend(): React.CSSProperties {
  return {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.04,
    color: 'var(--color-text-muted, #6b6b7d)',
    marginBottom: 8,
    padding: 0,
  };
}

function input(): React.CSSProperties {
  return {
    padding: '6px 10px',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 4,
    fontSize: 13,
    fontFamily: 'inherit',
  };
}

function badge(allFilled: boolean): React.CSSProperties {
  return {
    padding: '2px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    background: allFilled ? 'var(--color-success-bg, #e6f7ed)' : 'var(--color-blue-50, #e8edff)',
    color: allFilled ? 'var(--color-success, #2f9e44)' : 'var(--color-brand-blue, #3b5bdb)',
  };
}
